import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// Context
import { useBodyshopRateContext } from '../bodyshopRateContext';

// Components & Utils
import Box from '@/main/components/shared/box';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import * as S from './styles';

import { KeyValue } from '@/main/models/common';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';
import {
  getDefaultCountryOptionDrop,
  getDefaultOptionDrop,
  getTranslateByKey,
  isNullUndefined
} from '@/main/services/utils';
import { userIsInsurer } from '@/main/services/utilsUser';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';

// Reducers
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import {
  getInsurers,
  resetReducerInsurers,
  useInsurerReducer
} from '@/main/store/insurer';
import {
  getRateImportStatus,
  resetReducerRateImportStatus,
  useRateImportStatusReducer
} from '@/main/store/rateImportStatus';
import useFormQueryParams from '@/main/hooks/useformQueryParams';
import { RateImportStatusEnum } from '@/main/services/enum/RateImportStatusEnum';

export function BodyshopRateFilter() {
  const dispatch = useDispatch();
  const { getDataSearch, onResetSearch, showFilters, setShowFilters } =
    useBodyshopRateContext();

  const { countriesUser: countryReducer } = useCountryReducer();
  const { insurers: insurerReducer } = useInsurerReducer();
  const { rateImportStatus: rateImportStatusReducer } = useRateImportStatusReducer();

  const disabledCountry = countryReducer?.data?.length === 1;
  const disabledInsurer = userIsInsurer();

  const optionDefaultCountryDrop = getDefaultCountryOptionDrop(
    countryReducer.data
  );
  const optionDefaultInsurerDrop = userIsInsurer()
    ? getDefaultOptionDrop(insurerReducer.data)
    : null;
  const optionDefaultStatusDrop = rateImportStatusReducer.data?.find(
    item => Number(item.key) === RateImportStatusEnum.Success
  ) ?? null;

  const INITIAL_FORM_STATE = {
    country: optionDefaultCountryDrop as KeyValue | null,
    insurer: optionDefaultInsurerDrop as KeyValue | null,
    name: '',
    status: optionDefaultStatusDrop as KeyValue | null
  };

  type ISearchBodyshopRateForm = typeof INITIAL_FORM_STATE;

  const methods = useFormQueryParams<ISearchBodyshopRateForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE,
    mapDefaultValues: {
      country: countryReducer,
      insurer: insurerReducer,
      status: rateImportStatusReducer
    },
    onSubmit: onSubmit
  });

  const {
    control,
    setValue,
    getValues,
    reset,
    customHandleSubmit,
    watch
  } = methods;

  const watcherCountry = watch('country');

  useEffect(() => {
    if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
    if (isNullUndefined(rateImportStatusReducer.data)) dispatch(getRateImportStatus());

    return () => {
      dispatch(resetReducerInsurers());
      dispatch(resetReducerRateImportStatus());
    };
  }, []);

  useEffect(() => {
    if (rateImportStatusReducer.data) {
      const optionDefaultStatusDropAsync = rateImportStatusReducer.data.find(
        item => Number(item.key) === RateImportStatusEnum.Success
      );
      if (optionDefaultStatusDropAsync && !getValues('status')) {
        setValue('status', optionDefaultStatusDropAsync);
      }
    }
  }, [rateImportStatusReducer.data]);

  useEffect(() => {
    if (isNullUndefined(watcherCountry)) {
      setValue('insurer', null);
      dispatch(resetReducerInsurers());
      return;
    }

    dispatch(
      getInsurers({
        guidCountry: String(watcherCountry.key),
        idStatusRegister: StatusRegisterEnum.Active
      })
    );
  }, [watcherCountry?.key]);

  function handleClear() {
    reset(INITIAL_FORM_STATE);
    onResetSearch();
  }

  function onSubmit(data: typeof INITIAL_FORM_STATE) {
    getDataSearch({
      guidCountry: data.country ? String(data.country.key) : undefined,
      guidInsurer: data.insurer ? String(data.insurer.key) : undefined,
      rateImportName: data.name,
      idRateImportStatus: data.status ? Number(data.status.key) : undefined,
      pageIndex: PaginationEnum.PageIndexIntial,
      pageSize: PaginationEnum.PageSize10,
      tick: new Date().getTime()
    });
    setShowFilters(false);
  }

  return (
    <Box showBox={showFilters} title={getTranslateByKey('information.filters')}>
      <FormProvider {...(methods as any)}>
        <form onSubmit={customHandleSubmit} noValidate>
          <S.Container>
            <KogAutoCompleteControlled
              required
              id="id-bodyshop-select-country"
              nameCustom="country"
              control={control}
              label={getTranslateByKey('information.country')}
              options={countryReducer.data}
              isLoading={countryReducer.isLoading}
              rulesCustom={{ required: true }}
              setValueCustom={setValue}
              isDefaultValueUnique
              disabled={disabledCountry}
              onChangeCustom={() => {}}
            />

            <KogInputTextControlledNew
              name="name"
              label={getTranslateByKey('information.filename')}
              control={control}
              inputProps={{ maxLength: 100 }}
              id="id-bodyshop-txt-name"
            />

            <KogAutoCompleteControlled
              id="id-bodyshop-select-insurer"
              nameCustom="insurer"
              label={getTranslateByKey('information.insurer')}
              isDefaultValueUnique={optionDefaultInsurerDrop !== null}
              control={control}
              options={insurerReducer.data}
              isLoading={insurerReducer.isLoading}
              setValueCustom={setValue}
              disabled={disabledInsurer || isNullUndefined(watcherCountry)}
              onChangeCustom={() => {}}
            />

            <KogAutoCompleteControlled
              nameCustom="status"
              label={getTranslateByKey('information.status')}
              defaultValue={optionDefaultStatusDrop}
              control={control}
              options={rateImportStatusReducer.data}
              isLoading={rateImportStatusReducer.isLoading}
              setValueCustom={setValue}
              id="id-bodyshop-select-status"
              onChangeCustom={() => {}}
            />

            <S.AreaButtons>
              <SecondaryButton id="id-bodyshop-btn-submit" type="submit">
                {getTranslateByKey('information.search')}
              </SecondaryButton>
              <SecondaryButton
                id="id-bodyshop-btn-clear"
                type="button"
                onClick={handleClear}>
                {getTranslateByKey('information.clear')}
              </SecondaryButton>
            </S.AreaButtons>
          </S.Container>
        </form>
      </FormProvider>
    </Box>
  );
}

export default BodyshopRateFilter;
